import { useEffect } from 'react'

interface MapboxAttributionOptions {
  enabled?: boolean
  textColor?: string
  hoverColor?: string
  fontSize?: string
  padding?: string
}

/**
 * Global hook to camouflage Mapbox attribution across the application
 * This hook injects CSS to make the attribution barely visible but still legally compliant
 *
 * @param options - Configuration options for the attribution camouflage
 * @param options.enabled - Whether to enable the camouflage (default: true)
 * @param options.textColor - Custom text color (default: rgba(0, 0, 0, 0.3))
 * @param options.hoverColor - Custom hover color (default: rgba(0, 0, 0, 0.5))
 * @param options.fontSize - Custom font size (default: 10px)
 * @param options.padding - Custom padding (default: 2px 4px)
 */
export const useMapboxAttribution = ({
  enabled = true,
  textColor = 'rgba(0, 0, 0, 0.3)',
  hoverColor = 'rgba(0, 0, 0, 0.5)',
  fontSize = '10px',
  padding = '2px 4px',
}: MapboxAttributionOptions = {}) => {
  useEffect(() => {
    if (!enabled) return

    // Create unique style ID to prevent duplicates
    const styleId = 'mapbox-attribution-camouflage'

    // Check if style already exists
    if (document.getElementById(styleId)) {
      return
    }

    const style = document.createElement('style')
    style.id = styleId
    style.textContent = `
      .mapboxgl-ctrl-attrib {
        background: transparent !important;
        color: ${textColor} !important;
        font-size: ${fontSize} !important;
        padding: ${padding} !important;
      }
      .mapboxgl-ctrl-attrib a {
        color: ${textColor} !important;
        text-decoration: none !important;
      }
      .mapboxgl-ctrl-attrib a:hover {
        color: ${hoverColor} !important;
      }
      /* Hide Mapbox logo on bottom left */
      .mapboxgl-ctrl-logo {
        display: none !important;
      }
      .mapboxgl-ctrl-logo a {
        display: none !important;
      }
    `
    document.head.appendChild(style)

    return () => {
      // Only remove if this is the last instance
      const existingStyle = document.getElementById(styleId)
      if (existingStyle) {
        document.head.removeChild(existingStyle)
      }
    }
  }, [enabled, textColor, hoverColor, fontSize, padding])
}

export default useMapboxAttribution
